import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";

export interface PersonaOption {
  id: string;
  title: string;
  description: string;
  systemPrompt: string;
}

import type { PersonaOptionTemplate } from "../config/voicePersonaConfig";

export function buildPersonaOptions(templates: PersonaOptionTemplate[], language: string): PersonaOption[] {
  // Normalize language code to match keys (e.g. 'zh-CN' -> 'zh')
  // Keys in config are simple: 'en', 'zh', 'es'
  let langKey = 'en';
  if (language.startsWith('zh')) langKey = 'zh';
  else if (language.startsWith('es')) langKey = 'es';
  else if (language.startsWith('en')) langKey = 'en';

  return templates.map((option) => {
    // Priority:
    // 1. Localized text for current language (option.title['zh'])
    // 2. Fallback to English text (option.title['en'])
    // 3. Fallback to translation key (t(option.titleKey))
    // 4. Fallback to ID

    let title = option.id;
    if (option.title && option.title[langKey]) title = option.title[langKey];
    else if (option.title && option.title['en']) title = option.title['en'];

    let description = '';
    if (option.desc && option.desc[langKey]) description = option.desc[langKey];
    else if (option.desc && option.desc['en']) description = option.desc['en'];

    // Resolve System Prompt
    let systemPrompt = '';
    if (option.prompt && option.prompt[langKey]) systemPrompt = option.prompt[langKey];
    else if (option.prompt && option.prompt['en']) systemPrompt = option.prompt['en'];

    return {
      id: option.id,
      title,
      description,
      systemPrompt,
    };
  });
}

interface PersonaSelectorSheetProps {
  show: boolean;
  options: PersonaOption[];
  selectedPersonaId: string;
  onSelect: (id: string) => void;
  onClose: () => void;
  sheetTitle: string;
  currentLabel: string;
}

export function PersonaSelectorSheet({
  show,
  options,
  selectedPersonaId,
  onSelect,
  onClose,
  sheetTitle,
  currentLabel,
}: PersonaSelectorSheetProps) {
  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[9999]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
            onClick={onClose}
          />
          <motion.div
            className="absolute inset-x-0 bottom-0 backdrop-blur-2xl border-t border-white/20 rounded-t-[32px] px-6 pt-4 overflow-y-auto"
            style={{
              maxHeight: "80vh",
              paddingBottom:
                "calc(env(safe-area-inset-bottom, 0px) + 2rem)",
              background:
                "linear-gradient(to bottom, rgba(60, 136, 238, 0.95), rgba(107, 187, 252, 0.95))",
              zIndex: 9999,
            }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 260, damping: 25 }}
          >
            <div className="mx-auto w-12 h-1.5 rounded-full bg-white/30 mb-6" />
            <div className="mb-6 space-y-2 text-center">
              <h2 className="text-lg font-semibold text-white">{sheetTitle}</h2>
            </div>
            <div className="grid grid-cols-1 gap-3 overflow-y-auto pb-10">
              {options.map((option) => {
                const isSelected = option.id === selectedPersonaId;

                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => {
                      onSelect(option.id);
                    }}
                    className={`rounded-2xl px-4 py-4 transition-all flex flex-col items-center justify-center text-center gap-2 relative ${isSelected
                      ? "bg-transparent text-white border border-white/30"
                      : "bg-blue-800/40 text-white hover:bg-blue-800/50"
                      }`}
                  >
                    <span className="text-sm font-semibold leading-tight flex items-center gap-2">
                      {option.title}
                    </span>
                    <span className="text-xs text-white/70 line-clamp-2">
                      {option.description}
                    </span>
                    {isSelected && (
                      <span className="text-[10px] font-medium uppercase tracking-wide text-white/70">
                        {currentLabel}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      )
      }
    </AnimatePresence >,
    document.body
  );
}
